// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {APP_ZOOM_LEVEL_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {isGlassThemeActive} from '@app/features/theme/utils/GlassThemeUtils';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {ActiveNowTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/ActiveNowTab';
import {
	HdrTabContent,
	shouldShowHdrSettings,
} from '@app/features/user/components/modals/tabs/appearance_tab/AppearanceTabHdrTab';
import {ChannelListTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/ChannelListTab';
import {FontTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/FontTab';
import {
	canResetGlassEffect,
	GlassCustomiseAction,
	GlassEffectResetAction,
	GlassFineTuneContent,
	GlassTabContent,
} from '@app/features/user/components/modals/tabs/appearance_tab/GlassTab';
import {InterfaceTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/InterfaceTab';
import {MessagesTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/MessagesTab';
import {
	AppZoomLevelResetAction,
	AppZoomLevelTabContent,
	canResetAppZoomLevel,
} from '@app/features/user/components/modals/tabs/appearance_tab/ScalingTab';
import {SidebarTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/SidebarTab';
import {StreamerModeTabContent} from '@app/features/user/components/modals/tabs/appearance_tab/StreamerModeTab';
import {
	HighlightColorPicker,
	ThemeTabContent,
} from '@app/features/user/components/modals/tabs/appearance_tab/theme/ThemeTabContent';
import {StartPageSection} from '@app/features/user/components/modals/tabs/StartPageSection';
import {shouldShowAppZoomLevel} from '@app/features/user/components/settings_utils/AppZoomLevelUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

const FONT_DESCRIPTOR = msg({
	message: 'Font',
	comment: 'Appearance settings section title for the interface typeface.',
});
const THEME_DESCRIPTOR = msg({
	message: 'Theme',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const THEME_SECTION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Pick one; you can change it any time.',
	comment: 'One-line description under the Theme heading in Appearance settings.',
});
const GLASS_DESCRIPTOR = msg({
	message: 'Glass',
	comment: 'Heading of the glass theme controls in Appearance settings. Keep it concise.',
});
const GLASS_SECTION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Tone and look of the frosted panels.',
	comment: 'One-line description under the Glass heading in Appearance settings.',
});
const FINE_TUNE_DESCRIPTOR = msg({
	message: 'Fine-tune',
	comment: 'Heading of the everyday glass knobs (blur, floating panels, reduce transparency). Keep it concise.',
});
const FINE_TUNE_DESCRIPTION_DESCRIPTOR = msg({
	message: 'Only for Glass. Saved on this device.',
	comment: 'One-line description under the Fine-tune heading in Appearance settings.',
});
const MESSAGES_SECTION_DESCRIPTION_DESCRIPTOR = msg({
	message: 'How conversations are laid out.',
	comment: 'One-line description under the Messages heading in Appearance settings.',
});
const HIGHLIGHT_DESCRIPTOR = msg({
	message: 'Highlight',
	comment: 'Phone Appearance section label above the highlight colour swatches. Keep it concise.',
});
const HIGH_DYNAMIC_RANGE_DESCRIPTOR = msg({
	message: 'High dynamic range',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const MESSAGES_DESCRIPTOR = msg({
	message: 'Messages',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const INTERFACE_DESCRIPTOR = msg({
	message: 'Interface',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const CHANNEL_LIST_DESCRIPTOR = msg({
	message: 'Channel list',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const SIDEBAR_DESCRIPTOR = msg({
	message: 'Sidebar',
	comment: 'Appearance settings section title for the desktop icon sidebar. Keep it concise.',
});
const ACTIVE_NOW_DESCRIPTOR = msg({
	message: 'Active now',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
const STREAMER_MODE_DESCRIPTOR = msg({
	message: 'Streaming privacy',
	comment: 'Short label in the appearance tab. Keep it concise.',
});
export const AppearanceTab: React.FC = observer(() => {
	const {i18n} = useLingui();
	const showZoomLevel = shouldShowAppZoomLevel();
	const showHdrSettings = shouldShowHdrSettings();
	const isGlassTheme = isGlassThemeActive();
	const isMobile = MobileLayout.enabled;
	const messagesSection = (
		<SettingsSection
			id="messages"
			title={i18n._(MESSAGES_DESCRIPTOR)}
			description={isMobile ? undefined : i18n._(MESSAGES_SECTION_DESCRIPTION_DESCRIPTOR)}
			data-flx="user.appearance-tab.messages"
		>
			<MessagesTabContent data-flx="user.appearance-tab.messages-tab-content" />
		</SettingsSection>
	);
	// KONVO phone refresh (p1-settings): Theme, Highlight and Messages lead as three cards; the
	// theme cards are the preview on both layouts.
	return (
		<SettingsTabContainer data-flx="user.appearance-tab.settings-tab-container">
			<SettingsTabContent data-flx="user.appearance-tab.settings-tab-content">
				<SettingsSection
					id="theme"
					title={i18n._(THEME_DESCRIPTOR)}
					description={isMobile ? undefined : i18n._(THEME_SECTION_DESCRIPTION_DESCRIPTOR)}
					data-flx="user.appearance-tab.theme"
				>
					<ThemeTabContent data-flx="user.appearance-tab.theme-tab-content" />
				</SettingsSection>
				{isMobile ? (
					<SettingsSection
						id="highlight-colour"
						title={i18n._(HIGHLIGHT_DESCRIPTOR)}
						linkable={false}
						data-flx="user.appearance-tab.highlight"
					>
						<HighlightColorPicker showHeading={false} />
					</SettingsSection>
				) : null}
				{isMobile ? messagesSection : null}
				<SettingsSection id="font" title={i18n._(FONT_DESCRIPTOR)} linkable={false} data-flx="user.appearance-tab.font">
					<FontTabContent data-flx="user.appearance-tab.font-tab-content" />
				</SettingsSection>
				<SettingsSection
					id="glass-effect"
					title={i18n._(GLASS_DESCRIPTOR)}
					description={i18n._(GLASS_SECTION_DESCRIPTION_DESCRIPTOR)}
					actions={
						<>
							<GlassCustomiseAction data-flx="user.appearance-tab.glass-customise-action" />
							{canResetGlassEffect() ? (
								<GlassEffectResetAction data-flx="user.appearance-tab.glass-effect-reset-action" />
							) : null}
						</>
					}
					data-flx="user.appearance-tab.glass-effect"
				>
					<GlassTabContent data-flx="user.appearance-tab.glass-tab-content" />
				</SettingsSection>
				{isGlassTheme ? (
					<SettingsSection
						id="glass-fine-tune"
						title={i18n._(FINE_TUNE_DESCRIPTOR)}
						description={i18n._(FINE_TUNE_DESCRIPTION_DESCRIPTOR)}
						data-flx="user.appearance-tab.glass-fine-tune"
					>
						<GlassFineTuneContent data-flx="user.appearance-tab.glass-fine-tune-content" />
					</SettingsSection>
				) : null}
				{showHdrSettings ? (
					<SettingsSection id="hdr" title={i18n._(HIGH_DYNAMIC_RANGE_DESCRIPTOR)} data-flx="user.appearance-tab.hdr">
						<HdrTabContent data-flx="user.appearance-tab.hdr-tab-content" />
					</SettingsSection>
				) : null}
				{showZoomLevel ? (
					<SettingsSection
						id="app-zoom-level"
						title={i18n._(APP_ZOOM_LEVEL_DESCRIPTOR)}
						actions={
							canResetAppZoomLevel() ? (
								<AppZoomLevelResetAction data-flx="user.appearance-tab.app-zoom-level-reset-action" />
							) : null
						}
						data-flx="user.appearance-tab.app-zoom-level"
					>
						<AppZoomLevelTabContent data-flx="user.appearance-tab.app-zoom-level-tab-content" />
					</SettingsSection>
				) : null}
				{isMobile ? null : messagesSection}
				{isMobile ? null : <StartPageSection formFactor="desktop" data-flx="user.appearance-tab.start-page" />}
				{/* KONVO: the icon rail only exists on the desktop layout. */}
				{isMobile ? null : (
					<SettingsSection id="sidebar" title={i18n._(SIDEBAR_DESCRIPTOR)} data-flx="user.appearance-tab.sidebar">
						<SidebarTabContent data-flx="user.appearance-tab.sidebar-tab-content" />
					</SettingsSection>
				)}
				<SettingsSection id="interface" title={i18n._(INTERFACE_DESCRIPTOR)} data-flx="user.appearance-tab.interface">
					<InterfaceTabContent data-flx="user.appearance-tab.interface-tab-content" />
				</SettingsSection>
				<SettingsSection
					id="channel-list"
					title={i18n._(CHANNEL_LIST_DESCRIPTOR)}
					data-flx="user.appearance-tab.channel-list"
				>
					<ChannelListTabContent data-flx="user.appearance-tab.channel-list-tab-content" />
				</SettingsSection>
				<SettingsSection
					id="active-now"
					tabType="appearance"
					title={i18n._(ACTIVE_NOW_DESCRIPTOR)}
					data-flx="user.appearance-tab.active-now"
				>
					<ActiveNowTabContent data-flx="user.appearance-tab.active-now-tab-content" />
				</SettingsSection>
				<SettingsSection
					id="streamer-mode"
					title={i18n._(STREAMER_MODE_DESCRIPTOR)}
					data-flx="user.appearance-tab.streamer-mode"
				>
					<StreamerModeTabContent data-flx="user.appearance-tab.streamer-mode-tab-content" />
				</SettingsSection>
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});
